/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  currentCluster,
  currentClusterInfo,
  currentClusterNodes,
  currentClusterNotSystemCaches,
  currentTab,
  dashboardGroupByName,
  isCurrentClusterActive,
  isCurrentClusterOnline,
  isCurrentClusterUninitialized,
  selectRouteParam,
} from '@app/core/ngrx';
import { currentCluster3xSchemaTree } from '@app/core/ngrx/cluster-3x.selectors';
import { isCluster2xSelector } from '@app/core/ngrx/cluster.selectors';
import { Tab } from '@app/core/types';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isCluster2x } from '@shared/domain/cluster';
import {
  ClusterActionFailedResponse,
  ClusterActionRunningResponse,
  ResponseStatus,
} from '@shared/types/cluster-actions';
import { QueryType, ScanQuery, SqlQuery } from '@shared/types/queries';
import {
  CursorInfo3x,
  Query3xExecutionResult,
  Query3xExecutionResultError,
  Query3xExecutionResultOk,
} from '@shared/types/queries-3x';
import { flatten, fromPairs, groupBy, isEmpty, isMatch, memoize, omit } from 'lodash-es';
import { DEFAULT_SQL_SETTINGS } from '../default-query';
import { QUERIES_GROUP_NAME, isExplain } from '../domain/query';
import { convertResponseRow, getResponseColumnId } from '../helpers/convert-response-row.helper';
import { getStackedResults } from '../helpers/get-stacked-results.helper';
import {
  ConvertedQueryActionResponse,
  ConvertedQueryResponseResult,
  FolderName,
  QueriesState,
  ReasonForDisablingSQLExecution,
  TreeRoot,
} from '../types';

const queries = createFeatureSelector<QueriesState>('queries');

export const queriesSelector = createSelector(queries, (state) => state.sqlQueries);

export const responsesSelector = createSelector(queries, (state) => state.responses);

export const cursors3xSelector = createSelector(queries, (state) => state.cursors3x);

export const cursorPages3xSelector = createSelector(queries, (state) => state.cursorPages3x);

export const queryRuns3xSelector = createSelector(queries, (state) => state.queryRuns3x);

export const actionsSelector = createSelector(queries, (state) => state.actions);

export const exportsSelector = createSelector(queries, (state) => state.exports);

export const sqlMetadataSelector = createSelector(queries, (state) => state.sqlMetadata);

export const showAdvancedSettingsSelector = createSelector(queries, (state) => state.showSQLSidePanel.advancedSettings);

export const currentQuery = createSelector(queriesSelector, currentTab, (queries, tab) =>
  (queries || []).find((q) => q.id === tab),
);

export const hasParameters = createSelector(currentQuery, (query) => {
  if (!query || query.type === QueryType.SCAN) return false;
  return !isEmpty(query.parameters);
});

export const showParametersSelector = createSelector(
  hasParameters,
  queries,
  (hasParameters, state) => hasParameters && state.showSQLSidePanel.parameters,
);

export const queryById = (id: string) =>
  createSelector(queriesSelector, (queries) => (queries || []).find((q) => q.id === id));

export const runningActions = createSelector(actionsSelector, (actions) => actions[ResponseStatus.RUNNING] || []);

export const runningExports = createSelector(exportsSelector, (exports) => exports[ResponseStatus.RUNNING] || []);
export const canceledExports = createSelector(exportsSelector, (exports) => exports[ResponseStatus.CANCELED] || []);

export const runningQueries = createSelector(runningActions, runningExports, (actions, exports) => [
  ...actions.map(({ argument: { queryId } }) => queryId),
  ...exports.map(({ id }) => id),
]);

export const runningQueries3x = createSelector(queries, (queries) => queries.runningQueries3x);

export const currentQueryIsRunning = createSelector(
  currentQuery,
  runningQueries,
  runningQueries3x,
  isCluster2xSelector,
  (query, runningQueries, runningQueries3x, is2x) =>
    query ? (is2x ? runningQueries.includes(query.id) : runningQueries3x.includes(query.id)) : false,
);

export const currentQueryRunningType = createSelector(
  currentQuery,
  runningActions,
  runningExports,
  (query, actions, exports) =>
    query
      ? actions.some(({ argument: { queryId } }) => queryId === query.id)
        ? 'action'
        : exports.some(({ id }) => id === query.id)
          ? 'export'
          : false
      : false,
);

export const currentQueryId = createSelector(currentQuery, (query) => (query ? query.id : undefined));

export const convertedResponse2x = createSelector(responsesSelector, currentQueryId, (responses, id) => {
  if (!id) return null;
  const response = responses.get(id);
  if (!response) return null;
  switch (response.status) {
    case ResponseStatus.COMPLETED:
      return {
        ...response,
        result: (response.result || [])
          /* Because fixing empty responses on BE side may require core changes,
            it was decided to resolve it on FE side as a quick fix,
            so here we simply check for empty titles for each result and filter them out */
          .filter((r) => r.title)
          .map(
            (r, i) =>
              ({
                ...r,
                isExplain: isExplain(r),
                isScanQuery: r.type === QueryType.SCAN,

                columnDefs: r.columns.map((col, index) => ({
                  headerName: col.fieldName,
                  field: getResponseColumnId(col, index),
                  headerTooltip: col.typeName
                    ? `Column: ${col.fieldName}\nTable: ${col.typeName}\nType: ${col.fieldTypeName}`
                    : null,
                })),

                data: convertResponseRow(r),
              }) as ConvertedQueryResponseResult,
          ),
      };
    default:
      return { ...response } as ClusterActionRunningResponse | ClusterActionFailedResponse;
  }
});

const aggregate3xResults = (results: Query3xExecutionResultOk[], cursor: CursorInfo3x): ConvertedQueryResponseResult =>
  results.reduce(
    (acc: ConvertedQueryResponseResult, page) => {
      return {
        ...acc,
        columns: page.columns,
        columnDefs: !acc.columnDefs.length
          ? page.columns.map((col, index) => ({
              headerName: col.fieldName,
              field: getResponseColumnId(col, index),
              headerTooltip: col.typeName
                ? `Column: ${col.fieldName}\nTable: ${col.typeName}\nType: ${col.fieldTypeName}`
                : undefined,
            }))
          : acc.columnDefs,
        rows: [...acc.rows, ...page.rows],
        data: [
          ...acc.data,
          ...page.rows.map((row) => fromPairs(row.map((value, i) => [getResponseColumnId(page.columns[i], i), value]))),
        ],
        hasMore: page.hasMore,
      } satisfies ConvertedQueryResponseResult;
    },
    {
      failed: false,
      hasMore: false,
      duration: 0,
      cursorId: cursor.id,
      resultNodeId: '',
      title: cursor.statement,
      type: QueryType.SQL,
      id: cursor.id,
      isExplain: false,
      isScanQuery: false,
      columns: [],
      columnDefs: [],
      data: [],
      rows: [],
    } satisfies ConvertedQueryResponseResult,
  );

export const convertedResponse3x = createSelector(
  currentQueryId,
  queryRuns3xSelector,
  cursors3xSelector,
  cursorPages3xSelector,
  (queryId, allQueryRuns, allCursors, allCursorPages): ConvertedQueryActionResponse | null => {
    if (!queryId) return null;
    const queryRunId = allQueryRuns.get(queryId);
    if (!queryRunId) return null;
    const queryCursors = allCursors.get(queryRunId);
    if (!queryCursors) return null;
    const cursorPages: [CursorInfo3x, Query3xExecutionResult[] | undefined][] = queryCursors.map((cursor) => [
      cursor,
      allCursorPages.get(cursor.id),
    ]);
    const firstErrorPage = cursorPages
      .map(([, pages]) => pages)
      .flat()
      .filter((page): page is Query3xExecutionResultError => typeof page?.errorMessage === 'string')
      ?.at(0);

    if (firstErrorPage) {
      return {
        id: queryRunId,
        requestId: queryRunId,
        nodeConsistentId: '',
        timestamp: Date.now(),
        status: ResponseStatus.FAILED,
        error: {
          code: -1,
          message: firstErrorPage.errorMessage,
          stackTrace: [],
        },
      } satisfies ClusterActionFailedResponse;
    }
    const nonErrorResults = cursorPages.filter(
      (pair): pair is [CursorInfo3x, Query3xExecutionResultOk[]] => !!pair[1]?.some((page) => !page.errorMessage),
    );
    if (queryCursors.length !== nonErrorResults.length) {
      return {
        id: queryRunId,
        nodeConsistentId: '',
        requestId: queryRunId,
        status: ResponseStatus.RUNNING,
        timestamp: Date.now(),
      } satisfies ClusterActionRunningResponse;
    }
    return {
      id: queryRunId,
      requestId: queryRunId,
      nodeConsistentId: '',
      timestamp: 0,
      status: ResponseStatus.COMPLETED,
      result: getStackedResults(
        nonErrorResults
          .filter(([cursor]) => !!cursor.statement.trim())
          .map(([cursor, pages]) => aggregate3xResults(pages, cursor)),
      ),
    };
  },
);

export const convertedResponse = createSelector(
  isCluster2xSelector,
  convertedResponse2x,
  convertedResponse3x,
  (is2x, r2x, r3x) => (is2x ? r2x : r3x),
);

export const currentResponse = createSelector(responsesSelector, currentQuery, (responses, query) => {
  if (!query) return null;
  return responses.get(query.id);
});

export const currentResponseResultByCursorId = (cursorId: string) =>
  createSelector(currentResponse, (response) => {
    if (!response || response.status !== ResponseStatus.COMPLETED) return null;
    return response.result.find((r) => r.cursorId === cursorId);
  });

export const currentResponseResultByIndex3x = memoize((index: number) =>
  createSelector(convertedResponse3x, (response): ConvertedQueryResponseResult | null => {
    if (!response || response.status !== ResponseStatus.COMPLETED) return null;
    return response.result[index] ?? null;
  }),
);

export const currentResponseResultByIndex2x = (index: number) =>
  createSelector(currentResponse, (response) => {
    if (!response || response.status !== ResponseStatus.COMPLETED) return null;
    return response.result[index];
  });

export const currentResponseResultByIndex = memoize((index: number) =>
  createSelector(
    isCluster2xSelector,
    currentResponseResultByIndex2x(index),
    currentResponseResultByIndex3x(index),
    (is2x, r2x, r3x) => (is2x ? r2x : r3x),
  ),
);

export const queryTabs = createSelector(
  dashboardGroupByName(QUERIES_GROUP_NAME),
  queriesSelector,
  runningQueries,
  (group, queries, runningQueries) =>
    queries && group
      ? group.workspaceIds
          .map((id) => queries.find((q) => q.id === id))
          .filter((q): q is SqlQuery | ScanQuery => !!q)
          .map(
            (q) =>
              ({
                id: q.id,
                name: q.name,
                loading: runningQueries.includes(q.id),
                required: queries && queries.length === 1,
              }) as Tab,
          )
      : undefined,
);

export const currentClusterSqlMetadata = createSelector(currentCluster, sqlMetadataSelector, (cluster, sqlMetadata) =>
  cluster
    ? flatten(
        [...sqlMetadata.entries()].filter((entries) => entries[0].startsWith(cluster)).map((entries) => entries[1]),
      )
    : [],
);

export const currentClusterSchemaTree = createSelector(currentClusterSqlMetadata, (sqlMetadata) => {
  const schemas = Object.entries(
    groupBy(
      sqlMetadata.map((md) => ({
        schemaName: md.schemaName,
        name: md.tableName,
        fields: Object.entries(md.fields).map((f) => ({
          name: f[0],
          type: f[1],
        })),
      })),
      'schemaName',
    ),
  );

  return schemas.map((pair) => ({
    name: pair[0],
    tables: pair[1].map((p) => omit(p, 'schemaName')),
  }));
});

export const dbTree = createSelector(
  currentClusterInfo,
  currentClusterNotSystemCaches,
  currentClusterSchemaTree,
  currentClusterNodes,
  (cluster, caches, schemas, nodes): TreeRoot[] => {
    if (!cluster) return [];

    return [
      {
        id: cluster.id,
        name: cluster.tag,
        folders: [
          { name: FolderName.SCHEMA, metadata: schemas },
          { name: FolderName.CACHE, metadata: caches || [] },
          { name: FolderName.NODE, metadata: nodes.filter((n) => n.online) },
        ],
        loaded: !!caches,
      },
    ];
  },
);

export const cluster3xDbTree = createSelector(
  currentClusterInfo,
  currentCluster3xSchemaTree,
  (cluster, schemas): TreeRoot[] => {
    if (!cluster) return [];

    return [
      {
        id: cluster.id,
        name: cluster.tag,
        folders: [{ name: FolderName.SCHEMA, metadata: schemas }],
        loaded: !!schemas,
      },
    ];
  },
);

export const currentClusterSchemas = createSelector(currentClusterSqlMetadata, (sqlMetadata) => [
  ...new Set(sqlMetadata.map((meta) => meta.schemaName)),
]);

export const areAdvancedSettingsDefault = createSelector(currentQuery, (query) =>
  query && query.type === QueryType.SQL ? isMatch(query, DEFAULT_SQL_SETTINGS) : false,
);

export const reasonForDisablingExecution = createSelector(
  isCurrentClusterActive,
  isCurrentClusterUninitialized,
  isCurrentClusterOnline,
  currentClusterNodes,
  currentQuery,
  currentQueryIsRunning,
  isCluster2xSelector,
  (isActive, isUninitialized, isOnline, nodes, query, queryIsRunning, is2x) => {
    if (!isOnline) return ReasonForDisablingSQLExecution.CLUSTER_OFFLINE;
    if (typeof isUninitialized === 'boolean' && isUninitialized)
      return ReasonForDisablingSQLExecution.CLUSTER_UNINITIALIZED;
    if (typeof isActive === 'boolean' && !isActive) return ReasonForDisablingSQLExecution.CLUSTER_ACTIVE;
    if (queryIsRunning) return ReasonForDisablingSQLExecution.QUERY_IS_RUNNING;
    if (query && query.type === QueryType.SQL && is2x) {
      if (query.selectedNodeConsistentId) {
        const node = nodes.find((node) => node.consistentId === query.selectedNodeConsistentId);
        if (!node || !node.online) return ReasonForDisablingSQLExecution.INVALID_NODE;
      }
      if (Object.entries(query.parameters).some((p) => !p[1])) return ReasonForDisablingSQLExecution.EMPTY_PARAMETERS;
    }
    return null;
  },
);

export const doesClusterSupportQueryStatistics = createSelector(
  currentClusterInfo,
  (cluster) => !!cluster && isCluster2x(cluster),
);

export const currentClusterSupportsQueryCancellation = createSelector(isCluster2xSelector, (v) => v);

export const currentRunningQueryIdSelector = selectRouteParam('runningQueryId');
